import {
  Filter,
  FilterGroup,
  HeaderConfig,
  Row,
} from 'components/Table/KhulaSupplierTable';
import {
  IFarmInputProduct,
  IFarmInputProductInput,
  IFarmInputProductSubSubCategory,
} from 'constants/interfaces';
import React, {useState} from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {_NumberWithSpaces} from 'constants/index';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import {apiUpdateInputProduct} from 'constants/Api';
import {reduxUpdateProduct} from 'appredux/actions';

export const productTableHeaders = (args: {
  canPromote: boolean;
  canWrite: boolean;
}): HeaderConfig[] => [
  {
    field: 'id',
    title: 'Product',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
    maxWidth: 96,
    noWrap: true,
  },
  {
    field: 'stockCode',
    title: 'Stock Code',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
    hidden: true,
  },
  {
    field: 'activeIngredient',
    title: 'Active Ingredient',
    isSearchable: true,
    hidden: true,
  },
  {
    field: 'nameDisplay',
    title: 'Name',
    isSortable: true,
    sortField: 'name',
    isSearchable: true,
    searchField: 'name',
    isRightAligned: false,
  },
  {
    field: 'priceFormatted',
    title: 'Default Price',
    isSortable: true,
    sortField: 'price',
    isSearchable: false,
    isRightAligned: true,
  },
  {
    field: 'subCategoryName',
    title: 'Category',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  {
    field: 'size',
    title: 'Size',
    isSortable: true,
    isSearchable: true,
    isRightAligned: true,
  },
  {
    field: 'measurement',
    title: 'Meas',
    isSortable: true,
    isSearchable: true,
    isRightAligned: false,
  },
  ...(args.canPromote
    ? [
        {
          field: 'promotionActions',
          title: 'Promotion',
          isSortable: false,
          isSearchable: false,
          isCenterAligned: true,
        },
      ]
    : []),
  {
    field: 'productIsActiveCheckbox',
    title: 'Active',
    isSortable: true,
    sortField: 'isActive',
    isSearchable: false,
    isCenterAligned: true,
  },
  ...(args.canWrite
    ? [
        {
          field: 'actions',
          title: '',
          isSortable: false,
          isSearchable: false,
          isRightAligned: true,
        },
      ]
    : []),
];

export const productTableRows = (
  products: IFarmInputProduct[],
  subSubCategories: IFarmInputProductSubSubCategory[],
  onEdit: (product: IFarmInputProduct) => void,
  onCopy: (product: IFarmInputProduct) => void,
  onPromote: (product: IFarmInputProduct) => void,
  onCancelPromotion: (product: IFarmInputProduct) => void,
  canWrite: boolean,
): Row[] => {
  return products.map((product) => {
    // Provide an explicit `id` for the Row.
    const rowId = product.id; // numeric or string, whichever your row expects

    // Lookup subcategory name
    const subCatName = getProductCategoryName(product, subSubCategories);
    const mainCategoryName = getProductMainCategoryName(
      product,
      subSubCategories,
    );
    const supplierName = getProductSupplierName(product);

    return {
      /** Required "id" property for Row. */
      id: rowId,

      /** Additional columns. They must be string | number | boolean | Date | ReactNode, etc. */
      stockCode: product.stockCode,
      activeIngredient: product.activeIngredient || '',
      productType: product.productType || '',
      supplierName,
      name: product.name || '',
      nameDisplay: (
        <ProductNameCell product={product} onOpen={() => onEdit(product)} />
      ),
      size: product.size || '',
      measurement: product.measurement || '',
      subCategoryName: subCatName,
      mainCategoryName,

      /** A React element for the price */
      priceFormatted: (
        <ProductPrice
          retailPrice={product.price}
          promotedPrice={product.promotedPrice}
        />
      ),

      /** A React element for the Active icon */
      isActiveFormatted: product.isActive ? <CheckIcon /> : <CloseIcon />,

      /** The checkbox for toggling active/inactive */
      productIsActiveCheckbox: canWrite ? (
        <ProductIsActiveCheckbox product={product} disabled={false} />
      ) : product.isActive ? (
        <CheckIcon />
      ) : (
        <CloseIcon />
      ),

      /** If you need a hidden or boolean for searching/filtering by isPromoted: */
      isPromoted: Boolean(product.promotedPrice),

      promotionActions: (
        <PromotionActions
          isPromoted={!!product.promotedPrice}
          onPromote={() => onPromote(product)}
          onCancel={() => onCancelPromotion(product)}
        />
      ),

      actions: (
        <ProductRowIcons
          onEdit={() => onEdit(product)}
          onCopy={() => onCopy(product)}
        />
      ),

      /**
       * If you want searching/filtering by isInAllProducts or any other hidden fields,
       * define them as strings/booleans, or skip them if not needed in the row object.
       */
      isInAllProducts: true,
    };
  });
};

function ProductNameCell(props: {
  product: IFarmInputProduct;
  onOpen: VoidFunction;
}) {
  const {product} = props;

  return (
    <Box
      className="product-name-cell"
      onClick={props.onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') props.onOpen();
      }}
    >
      <Avatar
        src={product.image}
        variant="rounded"
        className="product-name-cell__thumbnail"
      >
        <BrokenImageIcon fontSize="small" />
      </Avatar>
      <span>{product.name || '-'}</span>
    </Box>
  );
}

function ProductPrice(props: {retailPrice: number; promotedPrice?: number}) {
  const discount = !props.promotedPrice
    ? 0
    : props.retailPrice - props.promotedPrice;
  const discountPercentage =
    discount === 0
      ? ''
      : (-(discount / props.retailPrice) * 100).toFixed(0) + '%';

  return (
    <div>
      <Badge
        className="discount-badge"
        invisible={!discount}
        badgeContent={discountPercentage}
      >
        R {_NumberWithSpaces(props.promotedPrice || props.retailPrice)}
      </Badge>
    </div>
  );
}

function ProductRowIcons(props: {onEdit: () => void; onCopy: () => void}) {
  return (
    <Box display="flex" flexDirection="row">
      <Tooltip title="Edit product" aria-label="edit product">
        <IconButton
          aria-label="edit product"
          size="small"
          onClick={props.onEdit}
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Copy product" aria-label="copy product">
        <IconButton
          aria-label="copy product"
          size="small"
          onClick={props.onCopy}
          style={{marginLeft: 8}}
        >
          <FilterNoneIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function PromotionActions(props: {
  isPromoted: boolean;
  onPromote: VoidFunction;
  onCancel: VoidFunction;
}) {
  return (
    <Box display="flex" flexDirection="row" justifyContent="center">
      {!props.isPromoted && (
        <Button
          className="promote-button"
          size="small"
          onClick={props.onPromote}
          variant="contained"
          disableElevation
        >
          Promote
        </Button>
      )}

      {props.isPromoted && (
        <Button
          className="cancel-button"
          size="small"
          onClick={props.onCancel}
          variant="contained"
          disableElevation
        >
          Cancel
        </Button>
      )}
    </Box>
  );
}

const ProductIsActiveCheckbox = (props: {
  product: IFarmInputProduct;
  disabled: boolean;
}) => {
  const [isActive, setIsActive] = useState(props.product.isActive);

  const handleIsActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedIsActive = event.target.checked;
    setIsActive(updatedIsActive);

    const update: Partial<IFarmInputProductInput> = {
      id: props.product.id,
      isActive: updatedIsActive,
    };

    apiUpdateInputProduct(update)
      .then((productUpdate) => {
        reduxUpdateProduct(productUpdate);
      })
      .catch((err) => {
        console.error('Failed to update product isActive', err);
        alert('Failed to update product. Please try again later');
        setIsActive(!updatedIsActive);
      });
  };

  return (
    <Checkbox
      checked={isActive}
      onChange={handleIsActiveChange}
      color="primary"
      disabled={props.disabled}
    />
  );
};

const getProductCategoryName = (
  product: IFarmInputProduct,
  subSubCategories: IFarmInputProductSubSubCategory[],
) =>
  subSubCategories.find((cat) => cat.id === product.subCategoryId)?.name || '';

const normaliseProductCategoryValue = (value?: string | number | null) =>
  value === undefined || value === null
    ? ''
    : String(value).trim().toLowerCase();

const getMainCategoryLabel = (value: string) => {
  if (!value) return '';
  if (value.includes('seedling')) return 'Seedling';
  if (value.includes('seed')) return 'Seed';
  if (
    value.includes('fertilizer') ||
    value.includes('fertiliser') ||
    value.includes('nutrient')
  )
    return 'Fertilizer';
  if (
    value.includes('chemical') ||
    value.includes('herbicide') ||
    value.includes('fungicide') ||
    value.includes('insecticide') ||
    value.includes('pesticide')
  )
    return 'Chemical';

  return '';
};

const getProductMainCategoryName = (
  product: IFarmInputProduct,
  subSubCategories: IFarmInputProductSubSubCategory[],
) => {
  const subCategoryName = getProductCategoryName(product, subSubCategories);
  const categoryValues = [
    product.productType,
    product.category,
    product.subCategoryName,
    subCategoryName,
  ]
    .map(normaliseProductCategoryValue)
    .filter(Boolean);

  for (const categoryValue of categoryValues) {
    const mainCategoryLabel = getMainCategoryLabel(categoryValue);
    if (mainCategoryLabel) return mainCategoryLabel;
  }

  return '';
};

const getProductSupplierName = (product: IFarmInputProduct) =>
  product.mainSupplier?.name || product.brand?.name || '';

/** Filter groups for "Active" and "Promoted" */
const activeFilterGroup: FilterGroup = {
  name: 'Status',
  filters: [
    {
      field: 'isActive',
      value: true,
      title: 'Active',
    },
    {
      field: 'isActive',
      value: false,
      title: 'Inactive',
    },
  ],
};

const promotedFilterGroup: FilterGroup = {
  name: 'Promotions',
  filters: [
    {
      field: 'isPromoted',
      value: true,
      title: 'Promoted Products',
    },
  ],
};

const uniqueFilterGroup = (
  name: string,
  field: string,
  values: (string | number | undefined | null)[],
): FilterGroup | undefined => {
  const uniqueValues = Array.from(
    new Set(
      values
        .map((value) =>
          value === undefined || value === null ? '' : String(value).trim(),
        )
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  if (!uniqueValues.length) return undefined;

  return {
    name,
    filters: uniqueValues.map((value) => ({
      field,
      value,
      title: value,
    })),
  };
};

const productSubCategoryFilterGroup = (
  products: IFarmInputProduct[],
  subSubCategories: IFarmInputProductSubSubCategory[],
): FilterGroup | undefined => {
  const filtersByKey = new Map<string, Filter>();

  products.forEach((product) => {
    const subCategoryName = getProductCategoryName(product, subSubCategories);
    const mainCategoryName = getProductMainCategoryName(
      product,
      subSubCategories,
    );
    if (!subCategoryName || !mainCategoryName) return;

    const key = `${mainCategoryName}-${subCategoryName}`;
    if (filtersByKey.has(key)) return;

    filtersByKey.set(key, {
      field: 'subCategoryName',
      value: subCategoryName,
      title: subCategoryName,
      parentField: 'mainCategoryName',
      parentValue: mainCategoryName,
    });
  });

  const filters = Array.from(filtersByKey.values()).sort((a, b) =>
    a.title.localeCompare(b.title),
  );

  if (!filters.length) return undefined;

  return {
    name: 'Sub-category',
    filters,
    dependsOn: 'Category',
  };
};

export const buildProductTableFilters = (
  products: IFarmInputProduct[],
  subSubCategories: IFarmInputProductSubSubCategory[],
  includePromotionFilter: boolean,
  includeStatusFilter: boolean,
): FilterGroup[] => {
  const categoryFilterGroup = uniqueFilterGroup(
    'Category',
    'mainCategoryName',
    products.map((product) =>
      getProductMainCategoryName(product, subSubCategories),
    ),
  );
  const dynamicFilters = [
    categoryFilterGroup,
    productSubCategoryFilterGroup(products, subSubCategories),
    uniqueFilterGroup(
      'Supplier',
      'supplierName',
      products.map((product) => getProductSupplierName(product)),
    ),
  ].filter(Boolean) as FilterGroup[];

  return [
    ...(includePromotionFilter ? [promotedFilterGroup] : []),
    ...dynamicFilters,
    ...(includeStatusFilter ? [activeFilterGroup] : []),
  ];
};

export const productTableFilters: FilterGroup[] = [
  promotedFilterGroup,
  activeFilterGroup,
];
